'use client';

import React, { useMemo } from 'react';
import { styled } from 'styled-components';
import { colors } from '../../abstracts';
import { StyledStepText, StyledStepSubtext, StyledStepTag, StepTextGroup, StyledStepLink, StyledMxLink } from './elements';
import { dontForwardProps } from '../../util';
const subTitleMaxHeight = 2.375;
const tagHeight = 1.25;
const getIconUrl = (state, size) => {
  const icon = {
    warning: 'warning.svg',
    complete: `checkmark-${size}.svg`
  }[state];
  return `https://assets.asurion-ui.my.asurion53.com/icons/component-specific/progress-stepper/${icon}`;
};
const calcPercentage = (part, total) => total > 0 ? 100 * part / total : 0;
const StyledStepListItem = styled.li.withConfig(dontForwardProps()).withConfig({
  componentId: "aui__sc-hcjic7-0"
})(["position:relative;border-radius:50%;border:0.125rem solid ", ";display:inline-flex;", " ", " ", " ", " &:not(:last-child){.progress-stepper-step-connector{display:block;}}"], colors.neutralDeeper, ({
  size
}) => {
  let diam;
  if (size === 'small') {
    diam = '0.75rem';
  } else if (size === 'medium') {
    diam = '1.25rem';
  }
  return `
      height: ${diam};
      width: ${diam};
      min-width: ${diam};
    `;
}, ({
  state
}) => state === 'inprogress' && `
      border: 0.125rem solid ${colors.black};
      .progress-stepper-step-inner-circle {
        opacity: 1;
      }
    `, ({
  state
}) => state === 'warning' && `
      border: 0.125rem solid ${colors.black};
      .progress-stepper-step-inner-circle {
        opacity: 1;
        transform: scale(1.66667);
        transition: all 0.3s;
      }
    `, ({
  state
}) => state === 'complete' && `
      border: 0.125rem solid ${colors.black};
      .progress-stepper-step-inner-circle {
        opacity: 1;
        transform: scale(2);
        transition: all 0.3s;
      }
    `);
const InnerCircle = styled.div.withConfig(dontForwardProps()).withConfig({
  componentId: "aui__sc-hcjic7-1"
})(["position:absolute;border-radius:50%;top:0;left:0;right:0;bottom:0;margin:auto;background-color:", ";opacity:0;transition:opacity 0.5s,scale 0.5s,color 0.3s;", ""], ({
  color
}) => color, ({
  size
}) => {
  let diam;
  if (size === 'small') {
    diam = '0.5rem';
  } else if (size === 'medium') {
    diam = '0.75rem';
  }
  return `
      height: ${diam};
      width: ${diam};
    `;
});
const StyledProgressIcon = styled.img.withConfig(dontForwardProps()).withConfig({
  componentId: "aui__sc-hcjic7-2"
})(["opacity:0;position:absolute;top:-0.125rem;right:-0.125rem;transform:scale(0.65);opacity:1;", ";transition:opacity 0.5s,scale 0.5s;", ""], ({
  state
}) => state == 'complete' && `filter: invert(1)`, ({
  size
}) => {
  let diam;
  if (size === 'small') {
    diam = '0.75rem';
  } else if (size === 'medium') {
    diam = '1rem';
  }
  return diam && `
        height: ${diam};
        width: ${diam};
      `;
});
const Connector = styled.div.withConfig(dontForwardProps()).withConfig({
  componentId: "aui__sc-hcjic7-3"
})(["background-color:", ";", ""], colors.neutralDeeper, ({
  orientation,
  size,
  isPacked
}) => orientation == 'vertical' ? `
          height: ${isPacked ? 'inherit' : `${(6.031 + subTitleMaxHeight + tagHeight).toFixed(2)}rem`};
          width: 0.125rem;
          margin-left: ${size == 'small' ? '0.438rem' : '0.688rem'};
        ` : `
          height: 0.125rem;
          width: 100%;
        `);
const ConnectorTextGroup = styled.div.withConfig(dontForwardProps()).withConfig({
  componentId: "aui__sc-hcjic7-4"
})(["display:flex;", ";"], ({
  orientation,
  isLast
}) => orientation == 'vertical' ? `
          flex-direction: row;
        ` : `
          flex-direction: column;
          ${isLast ? '' : 'width: 100%;'}
          margin-top: auto;
          margin-bottom: auto;
        `);
const ConnectorFill = styled.div.withConfig(dontForwardProps()).withConfig({
  componentId: "aui__sc-hcjic7-5"
})(["position:relative;", " background-color:", ";&:after{content:'';width:0.375rem;height:0.375rem;position:absolute;top:0;bottom:0;right:", ";border-radius:50%;margin:auto 0;background-color:currentColor;opacity:0;transition:opacity 0.5s;}", ""], ({
  orientation,
  connectorFillPercentage
}) => orientation == 'vertical' ? `
          height: ${connectorFillPercentage}%;
          width: 0.125rem;
          left: 0;
          transition: height 0.2s;
        ` : `
          width: ${connectorFillPercentage}%;
          height: 0.125rem;
          left: 0;
          transition: width 0.2s;
        `, colors.black, ({
  orientation
}) => orientation == 'vertical' ? '-0.125rem' : '-0.25rem', ({
  showDot
}) => showDot && `
      &:after {
        opacity: 1;
      }
    `);
export const ListItem = ({
  state = 'incomplete',
  subSteps = [],
  isPacked,
  stepTitle,
  stepSubTitle,
  stepLink,
  stepLinkText,
  isMxLink,
  tagType,
  tagText,
  tagIcon,
  tagFamily,
  index,
  stepsAmount,
  orientation,
  size = 'small'
}) => {
  const connectorFillPercentage = useMemo(() => {
    if (!Array.isArray(subSteps) || subSteps.length === 0) {
      return state === 'complete' ? 100 : 0;
    }
    if (subSteps[subSteps.length - 1].state === 'complete') {
      return 100;
    }
    const activeSubSteps = subSteps.filter(subStep => subStep.state !== 'incomplete').length;
    return calcPercentage(activeSubSteps, subSteps.length + 1);
  }, [state, subSteps]);
  const isLast = useMemo(() => index === stepsAmount - 1, [index, stepsAmount]);
  const showConnectorDot = useMemo(() => connectorFillPercentage > 0 && connectorFillPercentage < 100, [connectorFillPercentage]);
  const stateWithSubSteps = useMemo(() => state === 'complete' && connectorFillPercentage < 100 ? 'inprogress' : state, [connectorFillPercentage, state]);
  const StepLink = isMxLink ? StyledMxLink : StyledStepLink;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledStepListItem, {
    state: stateWithSubSteps,
    "data-testid": `progress-stepper-step-${index}`,
    size: size
  }, /*#__PURE__*/React.createElement(InnerCircle, {
    className: "progress-stepper-step-inner-circle",
    "data-testid": `progress-stepper-step-${index}-circle-${state}`,
    state: state,
    size: size,
    color: state == 'warning' ? colors.white : colors.black
  }, (state === 'complete' || state === 'warning') && /*#__PURE__*/React.createElement(StyledProgressIcon, {
    src: getIconUrl(state, size),
    alt: `Step ${state}`,
    state: stateWithSubSteps,
    size: size
  }))), /*#__PURE__*/React.createElement(ConnectorTextGroup, {
    isLast: isLast,
    orientation: orientation
  }, !isLast && /*#__PURE__*/React.createElement(Connector, {
    className: "progress-stepper-step-connector",
    size: size,
    isPacked: isPacked,
    stepsAmount: stepsAmount,
    orientation: orientation,
    "data-testid": `progress-stepper-step-${index}-connector`
  }, /*#__PURE__*/React.createElement(ConnectorFill, {
    showDot: showConnectorDot,
    connectorFillPercentage: connectorFillPercentage,
    orientation: orientation,
    "data-testid": `progress-stepper-step-${index}-connector-fill`
  })), orientation == 'vertical' && /*#__PURE__*/React.createElement(StepTextGroup, {
    isLast: isLast,
    size: size
  }, stepTitle && /*#__PURE__*/React.createElement(StyledStepText, {
    forwardedAs: "h2",
    size: 3,
    iconSize: size,
    weight: "feather",
    "data-testid": `step-title-${index}`
  }, stepTitle), stepSubTitle && /*#__PURE__*/React.createElement(StyledStepSubtext, {
    forwardedAs: 'h3',
    size: 2,
    iconSize: size,
    weight: "base",
    "data-testid": `step-subtitle-${index}`
  }, stepSubTitle), tagText && /*#__PURE__*/React.createElement(StyledStepTag, {
    size: "regular",
    type: tagType,
    text: tagText,
    iconSrc: tagIcon,
    family: tagFamily
  }), stepLinkText && (stepLink === null || stepLink === void 0 ? void 0 : stepLink.href) && /*#__PURE__*/React.createElement(StepLink, stepLink, stepLinkText))));
};